import { useState } from 'react'
import { useAdminAuditLog } from '../../hooks/useAdmin'
import type { AuditLogEntry } from '../../types/admin'

const GREEN  = { color: '#1e7e34', bg: '#E6F4EA' }
const RED    = { color: '#7A2A1E', bg: '#FAEBE7' }
const AMBER  = { color: '#856404', bg: '#FFF3CD' }
const PURPLE = { color: '#5a3e99', bg: '#EEE8FF' }
const TEAL   = { color: '#0C5460', bg: '#D1ECF1' }
const SLATE  = { color: '#3a4657', bg: '#EEF1F5' }

const ACTION_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  // Users
  'user.create':            { label: 'Created user',          ...GREEN },
  'user.update':            { label: 'Updated user',          ...SLATE },
  'user.delete':            { label: 'Deleted user',          ...RED },
  'user.restore':           { label: 'Restored user',         ...GREEN },
  // Churches
  'church.create':          { label: 'Created church',        ...GREEN },
  'church.update':          { label: 'Updated church',        ...SLATE },
  'church.slug_change':     { label: 'Changed church URL',    ...AMBER },
  'church.delete':          { label: 'Deleted church',        ...RED },
  'church.restore':         { label: 'Restored church',       ...GREEN },
  'church.member_add':      { label: 'Added member',          ...GREEN },
  'church.member_role':     { label: 'Changed member role',   ...PURPLE },
  'church.member_remove':   { label: 'Removed member',        ...RED },
  'church.campus_add':      { label: 'Added campus',          ...GREEN },
  'church.campus_update':   { label: 'Updated campus',        ...SLATE },
  'church.campus_remove':   { label: 'Removed campus',        ...RED },
  'church.calendar_import': { label: 'Imported calendar',     ...TEAL },
  'church.claim_request':   { label: 'Requested claim',       ...TEAL },
  'church.claim_approve':   { label: 'Approved claim',        ...GREEN },
  'church.claim_deny':      { label: 'Denied claim',          ...RED },
  'church.outreach':        { label: 'Sent outreach email',   ...TEAL },
  'church.scrape':          { label: 'Scraped church',        ...SLATE },
  'church.discover':        { label: 'Discovered churches',   ...SLATE },
  'church.enrich':          { label: 'Enriched church',       ...SLATE },
  // Subscriptions / billing
  'billing.subscribe':      { label: 'Started subscription',  ...GREEN },
  'billing.switch_plan':    { label: 'Switched plan',         ...PURPLE },
  'billing.resume':         { label: 'Resumed subscription',  ...GREEN },
  'billing.cancel':         { label: 'Canceled subscription', ...RED },
  // Events
  'event.create':           { label: 'Created event',         ...GREEN },
  'event.update':           { label: 'Updated event',         ...SLATE },
  'event.publish':          { label: 'Published event',       ...GREEN },
  'event.unpublish':        { label: 'Unpublished event',     ...AMBER },
  'event.delete':           { label: 'Deleted event',         ...RED },
  'event.restore':          { label: 'Restored event',        ...GREEN },
  // Event tags
  'event_tag.create':       { label: 'Created tag',           ...GREEN },
  'event_tag.update':       { label: 'Updated tag',           ...SLATE },
  'event_tag.deactivate':   { label: 'Deactivated tag',       ...AMBER },
  // Settings
  'settings.email_toggle':  { label: 'Toggled email sending', ...AMBER },
}

const TARGET_TYPES = ['', 'church', 'subscription', 'event', 'user']
const TARGET_LABELS: Record<string, string> = { '': 'All', church: 'Churches', subscription: 'Billing', event: 'Events', user: 'Users' }

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function DiffBlock({ entry }: { entry: AuditLogEntry }) {
  const keys = Array.from(new Set([
    ...Object.keys(entry.before ?? {}),
    ...Object.keys(entry.after ?? {}),
  ]))
  if (keys.length === 0) return null

  return (
    <div className="mt-2 flex gap-2.5 flex-wrap">
      {keys.map((k) => {
        const before = entry.before?.[k]
        const after = entry.after?.[k]
        const changed = JSON.stringify(before) !== JSON.stringify(after)
        return (
          <div key={k} className="text-[11px] text-ink-2">
            <span className="text-muted">{k}: </span>
            {entry.before && before !== undefined && (
              <span className={`mr-1 ${changed ? 'line-through text-[#999]' : 'text-ink-2'}`}>
                {String(before)}
              </span>
            )}
            {changed && entry.after && after !== undefined && (
              <span className="font-semibold text-ink">{String(after)}</span>
            )}
            {!entry.before && entry.after && after !== undefined && (
              <span className="font-semibold text-ink">{String(after)}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function AdminAuditLogPage() {
  const [page, setPage] = useState(1)
  const [targetType, setTargetType] = useState('')

  const { data, isLoading } = useAdminAuditLog({ targetType: targetType || undefined, page })
  const entries = data?.data ?? []
  const total = data?.meta.total ?? 0
  const totalPages = Math.ceil(total / 30)

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Audit Log</h1>
          <div className="text-[13px] text-muted mt-1">{total} actions recorded</div>
        </div>
        <div className="flex gap-1.5">
          {TARGET_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => { setTargetType(t); setPage(1) }}
              className={`px-3 py-[5px] rounded-lg text-[12px] font-medium border border-line-soft cursor-pointer font-sans ${targetType === t ? 'bg-ink text-surface' : 'bg-surface text-ink'}`}
            >
              {TARGET_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted text-[14px] py-10">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-muted text-[14px] py-10 text-center">No actions recorded yet.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {entries.map((entry) => {
            const meta = ACTION_LABELS[entry.action] ?? { label: entry.action, color: 'var(--ink)', bg: 'var(--surface-2)' }
            return (
              <div
                key={entry.id}
                className="px-[18px] py-3.5 rounded-xl bg-surface border border-line-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span
                      className="px-[9px] py-0.5 rounded-md text-[11px] font-semibold"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[12px] text-ink-2">
                      by <strong>{entry.actorDisplayName ?? entry.actorEmail ?? 'Unknown'}</strong>
                    </span>
                    <span className="text-[11px] px-[7px] py-0.5 rounded-[5px] bg-s2 text-muted">
                      {entry.targetType}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted whitespace-nowrap mt-0.5">
                    {formatDateTime(entry.createdAt)}
                  </div>
                </div>
                <DiffBlock entry={entry} />
              </div>
            )
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">← Prev</button>
          <span className="text-[13px] text-muted self-center">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-line-soft bg-surface text-ink text-[13px] cursor-pointer font-sans disabled:opacity-40">Next →</button>
        </div>
      )}
    </div>
  )
}
